// application/ports/serviceCodeErrors.js
//
// Three error classes in one file, against the usual one-per-file rule and on the same terms as
// `versionErrors`: they are the failure modes of one capability, a caller distinguishing them
// imports all three, and splitting them to satisfy a file-shape rule would make them harder to read.
/* eslint-disable max-classes-per-file */

/**
 * The ways authoring an org's own service code can fail.
 *
 * WHY THESE ARE PART OF THE PORT rather than raised as strings by the adapter. Each one needs its own
 * wording on screen and its own recovery, and Firestore gives the caller nothing to tell them apart:
 * a duplicate mint arrives as `permission-denied` from the create-only rule, which reads identically
 * to "you are not a manager at this clinic". The adapter has to narrow that before a screen can say
 * anything true about it.
 *
 * NONE OF THESE IS RETRYABLE AS SENT. Unlike `VersionCollision`, where the fix is to recompute and
 * try again, every failure here needs the user to change something or to stop.
 */

/**
 * The code string cannot be stored as typed.
 *
 * Carries the machine-readable reason from `codeStringRejection` rather than a sentence, because the
 * three cases — empty, too long, characters a document id cannot hold — want three different
 * messages next to the field, and composing them is the view's job.
 */
export class CodeStringRejected extends Error {
  /**
   * @param {string} reason - a `codeStringRejection` member
   * @param {*} code - the string as typed, for the message only
   */
  constructor(reason, code) {
    super(`The code ${JSON.stringify(code)} cannot be stored: ${reason}.`);
    this.name = 'CodeStringRejected';
    this.reason = reason;
  }
}

/**
 * This clinic already holds that code starting on that date.
 *
 * THE DATE IS PART OF THE QUESTION. A clinic may hold the same code more than once — a funder that
 * revises what a code covers reuses the string for a new period, which is two records in disjoint
 * windows. Only a second record starting on the SAME day is a duplicate, and the derived document id
 * is what makes that one unrepresentable rather than merely inadvisable.
 *
 * NOT the shadow case, which is advisory and never reaches here: that is a clinic's code colliding
 * with one WE govern, which is legitimate and only ever advised against. Nor is it a second period,
 * which is legitimate too and is flagged on screen rather than refused.
 *
 * It also covers the two-tabs race: both writers derive the same id, and the loser gets this instead
 * of a bare permission error.
 */
export class ServiceCodeAlreadyExists extends Error {
  /**
   * @param {string} code - the normalized code string
   * @param {string} validFrom - 'YYYY-MM-DD'
   */
  constructor(code, validFrom) {
    super(`This clinic already has a code ${code} starting ${validFrom}.`);
    this.name = 'ServiceCodeAlreadyExists';
    this.code = code;
    this.validFrom = validFrom;
  }
}

/**
 * The code cannot take this lifecycle change — retired, removed, or a managed code at all.
 *
 * Carries the reason token from the governance rules, for the same reason `CodeStringRejected` does:
 * "it has been referenced" and "it is one of ours" are different things to say to somebody, and only
 * the caller knows where on screen to say them.
 */
export class ServiceCodeChangeRejected extends Error {
  /**
   * @param {string} reason - a `serviceCodeRemovalRejection` / `serviceCodeRetirementRejection` member
   * @param {string|null} serviceCodeId
   */
  constructor(reason, serviceCodeId = null) {
    super(`This code cannot be changed: ${reason}.`);
    this.name = 'ServiceCodeChangeRejected';
    this.reason = reason;
    this.serviceCodeId = serviceCodeId;
  }
}
